import { START_LOCATION } from 'vue-router'
import type { RouteLocationNormalized, RouteLocationNormalizedLoaded } from 'vue-router'
import { routeName } from '@/constants/route.constants'
import { RegexConstants } from '@/constants/regex.constants'
import { toTagType } from '@/constants/tag-type.constants'

/**
 * Router guard for tag routes. The hostname getter is injected so tests need not stub jsdom's
 * `location`.
 *
 * 1. paw-f.com entry: the paw-f host only serves stamped tags, so a first load of the QR entry
 *    routes there opens the stamped-tag flow instead. In-app navigation is left alone.
 * 2. Tag type: named navigation skips the path regex, so an unknown `:tagType` aborts here, and a
 *    wrongly-cased one is redirected to the canonical casing.
 */
export function createTagRouteGuard(getHostname: () => string) {
  return (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded) => {
    if (from === START_LOCATION && RegexConstants.PAW_F_HOST.test(getHostname())) {
      if (to.name === routeName.public.scan) {
        return { name: routeName.public.stampedScan }
      }
      if (to.name === routeName.public.findTag) {
        return { name: routeName.public.stampedFindTag, params: { tagCode: to.params.tagCode } }
      }
    }

    const rawTagType = to.params.tagType
    if (typeof rawTagType === 'string') {
      const tagType = toTagType(rawTagType)
      if (!tagType) {
        throw new Error(`Unknown tag type: ${rawTagType}`)
      }
      // Every route with a :tagType segment is named, so the redirect can keep the name.
      if (tagType !== rawTagType && to.name) {
        return {
          name: to.name,
          params: { ...to.params, tagType },
          query: to.query,
          hash: to.hash,
          replace: true
        }
      }
    }

    return true
  }
}
